import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type BillingMe, type Client, type UserOut } from '../lib/api';
import { SequencerKeysEditor } from './Clients';

/** Workspace settings: workspace name + billing snapshot + (stub) Stripe
 * portal button. Visible to operators (their own workspace) and admins
 * (their attached workspace, if any). */
export default function Settings() {
  const me = useQuery({ queryKey: ['users-me'], queryFn: api.userMe });
  const billing = useQuery<BillingMe>({ queryKey: ['billing-me'], queryFn: api.billingMe, retry: false });

  const u = me.data && 'role' in me.data ? (me.data as UserOut) : null;
  const isAdmin = u?.role === 'admin';
  const isOperator = u?.role === 'operator';

  const [workspace, setWorkspace] = useState<Client | null>(null);
  const [name, setName] = useState('');
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [portalErr, setPortalErr] = useState<string | null>(null);

  useEffect(() => {
    if (!u?.client_id) return;
    api.getClient(u.client_id).then(c => { setWorkspace(c); setName(c.name); }).catch(() => {});
  }, [u?.client_id]);

  async function saveName() {
    if (!u?.client_id) return;
    setBusy(true); setSaveErr(null); setSaveOk(false);
    try {
      const updated = await api.patchClient(u.client_id, { name });
      setWorkspace(updated);
      setSaveOk(true);
    } catch (e: any) { setSaveErr(e.message); }
    finally { setBusy(false); }
  }

  async function openPortal() {
    setPortalErr(null);
    try {
      // The endpoint returns 501 until Stripe is configured.
      await fetch('/api/billing/portal', { method: 'POST', credentials: 'include' })
        .then(async r => {
          if (!r.ok) throw new Error((await r.text()) || `${r.status}`);
          const data = await r.json();
          if (data?.url) window.location.href = data.url;
        });
    } catch (e: any) {
      setPortalErr('Billing portal not yet available. Stripe wiring is in progress.');
    }
  }

  return (
    <div className="p-8 max-w-3xl space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Settings</h1>
        <p className="text-xs text-dim mt-1">
          Workspace name and billing for this Sendepot account.
        </p>
      </div>

      {/* Workspace card */}
      <div className="card p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-dim">Workspace</div>
          {isAdmin && <span className="pill-dim font-mono">admin · all workspaces</span>}
          {isOperator && <span className="pill-ok font-mono">operator</span>}
        </div>
        {workspace ? (
          <>
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider text-dim mb-1.5">Name</div>
              <div className="flex gap-2">
                <input className="input flex-1" value={name} onChange={e => setName(e.target.value)} />
                <button onClick={saveName} disabled={busy || !name.trim() || name === workspace.name} className="btn-primary">
                  {busy ? 'Saving…' : 'Save'}
                </button>
              </div>
              {saveErr && <div className="text-err text-xs mt-2">{saveErr}</div>}
              {saveOk && <div className="text-ok text-xs mt-2">Saved.</div>}
            </label>
            <Row k="Slug"   v={<span className="font-mono text-xs">{workspace.slug}</span>} />
            <Row k="Tenants" v={<span className="tabular-nums">{workspace.tenant_count}</span>} />
          </>
        ) : (
          <div className="text-dim text-sm">
            {isAdmin ? 'No workspace attached — you have cross-workspace access.' : 'No workspace.'}
          </div>
        )}
      </div>

      {/* Billing card */}
      <div className="card p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-dim">Billing</div>
          <span className={`pill-${billing.data?.subscription_status === 'active' ? 'ok'
            : billing.data?.subscription_status === 'trialing' ? 'warn'
            : 'dim'}`}>
            {billing.data?.subscription_status || '—'}
          </span>
        </div>
        {billing.isLoading && <div className="text-dim text-sm">Loading…</div>}
        {billing.data && (
          <>
            <Row k="Plan" v={<span className="font-mono text-xs">{billing.data.plan}</span>} />
            <Row k="Tenants" v={
              <span className="tabular-nums text-xs">
                {billing.data.tenants_used}
                {billing.data.tenant_cap !== null ? ` / ${billing.data.tenant_cap}` : ' (unlimited)'}
              </span>
            } />
            <Row k="Mailboxes" v={
              <span className="tabular-nums text-xs">
                {billing.data.mailboxes_used}
                {billing.data.mailbox_cap !== null ? ` / ${billing.data.mailbox_cap}` : ' (unlimited)'}
              </span>
            } />
            <Row k="Trial ends" v={
              billing.data.trial_ends_at
                ? <span className="tabular-nums text-xs">{new Date(billing.data.trial_ends_at).toLocaleString()}</span>
                : <span className="text-dim">—</span>
            } />
            <Row k="Next renewal" v={
              billing.data.current_period_end
                ? <span className="tabular-nums text-xs">{new Date(billing.data.current_period_end).toLocaleString()}</span>
                : <span className="text-dim">—</span>
            } />
            <div className="pt-3 flex items-center gap-3">
              <button onClick={openPortal} className="btn-primary">
                Manage billing →
              </button>
              <span className="text-[11px] text-dim">Stripe portal · {billing.data.has_stripe_customer ? 'linked' : 'not linked yet'}</span>
            </div>
            {portalErr && <div className="text-warn text-xs">{portalErr}</div>}
          </>
        )}
      </div>

      {/* Data export card */}
      <div className="card p-5 space-y-3">
        <div className="text-xs uppercase tracking-wider text-dim">Data export</div>
        <div className="text-sm text-ink">
          Download a portable zip of everything in your workspace:
          tenants, mailboxes, and audit log. Secrets (admin passwords,
          sequencer API keys, mailbox passwords) are intentionally
          excluded.
        </div>
        <div>
          <a href="/api/billing/export.zip" className="btn-secondary inline-block">
            Download workspace.zip
          </a>
        </div>
      </div>

      {/* Sequencer keys card */}
      {u?.client_id && (
        <div className="card p-5 space-y-3">
          <div className="text-xs uppercase tracking-wider text-dim">Sequencer API keys</div>
          <SequencerKeysEditor clientId={u.client_id} />
        </div>
      )}

      {/* Account card */}
      {u && (
        <div className="card p-5 space-y-3">
          <div className="text-xs uppercase tracking-wider text-dim">Account</div>
          <Row k="Email" v={<span className="font-mono text-xs">{u.email}</span>} />
          <Row k="Name"  v={u.name || <span className="text-dim">—</span>} />
          <Row k="Role"  v={<span className="font-mono text-xs">{u.role}</span>} />
        </div>
      )}

      {isAdmin && <AdminMagicLinkResend />}
    </div>
  );
}


/** Admin tool — force-send a fresh magic link to any user. Bypasses the
 * 60-second per-user cooldown that applies to the public /request
 * endpoint, so we can manually unblock customers whose link landed in
 * spam. Admin-only by virtue of the parent gating + a server-side
 * is_admin check. */
function AdminMagicLinkResend() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  async function send() {
    if (!email || busy) return;
    setBusy(true); setMsg(null);
    try {
      await api.adminResendMagicLink(email);
      setMsg({ kind: 'ok', text: `Sign-in link sent to ${email}.` });
      setEmail('');
    } catch (e: any) {
      let text = e?.message || 'Send failed.';
      try {
        const detail = JSON.parse((e.message as string).split(': ').slice(1).join(': ')).detail;
        if (detail) text = detail;
      } catch { /* keep raw */ }
      setMsg({ kind: 'err', text });
    } finally { setBusy(false); }
  }

  return (
    <div className="card p-5 space-y-3">
      <div className="flex items-baseline justify-between">
        <div className="text-xs uppercase tracking-wider text-dim">Resend sign-in link (admin)</div>
        <span className="text-[10px] text-dim">bypasses 60s cooldown</span>
      </div>
      <p className="text-xs text-dim leading-relaxed">
        Send a fresh magic link to a user — useful when their link got
        filtered to spam.
      </p>
      <div className="flex gap-2">
        <input
          className="input flex-1"
          type="email" placeholder="user@company.com"
          value={email} onChange={e => setEmail(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') send(); }}
        />
        <button onClick={send} disabled={busy || !email} className="btn-primary">
          {busy ? 'Sending…' : 'Send link'}
        </button>
      </div>
      {msg && (
        <div className={`text-xs flex items-start gap-1.5 ${msg.kind === 'ok' ? 'text-ok' : 'text-err'}`}>
          <span className={`dot ${msg.kind === 'ok' ? 'dot-ok' : 'dot-err'} mt-1`} />{msg.text}
        </div>
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-line/40 last:border-b-0">
      <div className="text-xs uppercase tracking-wider text-dim">{k}</div>
      <div className="text-sm">{v}</div>
    </div>
  );
}
